import React from 'react';

import { AMOUNT_FROM, AMOUNT_TO, CURRENCY_RATE_TEXT, FEE_TEXT } from 'constants/fieldsName';

import { isNotEmptyArray } from 'helpers/utils.js';

const getFormPropName = (isSend) => (isSend ? 'paymentForm' : 'requestForm');

/**
 * @param cur
 * @param tariff
 * @returns {number}
 */
const calcCommissionBack = (cur, tariff) => {
    let amount = cur - tariff.fix;
    if (tariff.percent > 0) {
        amount /= 1 + tariff.percent / 100;
    }

    return cur - amount;
};

/**
 * @param cur
 * @param tariff
 * @returns {number|*}
 */
const calcCommToOneCurrency = (cur, tariff) => {
    if (tariff.percent > 0) {
        return cur * (tariff.percent / 100);
    }

    return tariff.fix;
};

/**
 * @param tariffs
 * @param currentVal
 * @param isBack
 * @returns {null}
 */
const findTariff = (tariffs, currentVal, isBack) => {
    let tariff = null;

    if (!!tariffs) {
        if (tariffs.length > 1) {
            let nearestMax = null;
            let nearestMin = null;

            for (let item of tariffs) {
                let possibleVal = currentVal;
                if (isBack) {
                    possibleVal = currentVal - calcCommissionBack(currentVal, item);
                }
                if (possibleVal >= item.from && possibleVal <= item.to) {
                    tariff = item;
                } else {
                    if (possibleVal < item.from) {
                        if (nearestMin === null || nearestMin.from > item.from) {
                            nearestMin = item;
                        }
                    }
                    if (possibleVal > item.to) {
                        if (nearestMax === null || nearestMax.to < item.to) {
                            nearestMax = item;
                        }
                    }
                }
            }

            if (tariff === null) {
                tariff = nearestMin !== null ? nearestMin : nearestMax;
            }
        } else {
            tariff = tariffs[0];
        }
    }

    return tariff;
};

/**
 * @param newFee
 * @param feeText
 * @param oldFeeText
 * @returns {*}
 */
const getNewFeeText = (newFee, feeText, oldFeeText) => {
    const regex = /\d*.\d{2}(?=\s*\w+)/;
    if (newFee && feeText) {
        const newRateFixed = newFee.toFixed(2);
        return feeText.replace(regex, newRateFixed);
    }
    if (newFee && !feeText && oldFeeText) {
        const newRateFixed = newFee.toFixed(2);
        return oldFeeText.replace(regex, newRateFixed);
    }
};

export class CalculatorLandingService {
    constructor(isSend, formName, commission, helpForm, toggleTariffPopup, updateCalculatorFieldValue, changeFormFieldValue, messages) {
        this.isSend = isSend;
        this.formName = formName;
        this.commission = commission;
        this.helpForm = helpForm;
        this.toggleTariffPopup = toggleTariffPopup;
        this.updateCalculatorFieldValue = updateCalculatorFieldValue;
        this.changeFormFieldValue = changeFormFieldValue;
        this.messages = messages;
    }

    /**
     * @param value
     * @param objectRate
     * @param isFrom
     * @param currRateText
     * @param comm
     * @returns {*|number}
     */
    calcBySpecialRateOffer(value, objectRate, isFrom, currRateText, comm) {
        const { defaultRate, offers } = objectRate;

        if (!isFrom) {
            value = value / defaultRate + comm;
        }

        const selectedOfferArr = offers.filter((offer) => value >= offer.from && value <= offer.till);

        if (isNotEmptyArray(selectedOfferArr)) {
            const newRate = selectedOfferArr[0].rate;
            const newCurrRateText = this.getNewCurrRateText(newRate, currRateText);
            if (currRateText !== newCurrRateText) {
                this.updateCalculatorFieldValue(getFormPropName(this.isSend), CURRENCY_RATE_TEXT, newCurrRateText);
            }
            return newRate;
        } else {
            const newDefaultRateText = this.getNewCurrRateText(defaultRate, currRateText);
            this.updateCalculatorFieldValue(getFormPropName(this.isSend), CURRENCY_RATE_TEXT, newDefaultRateText);
            return defaultRate;
        }
    }

    /**
     * @param newRate
     * @param currRateText
     * @returns {*}
     */
    getNewCurrRateText(newRate, currRateText) {
        const regex = /\d+\.\d{4}(?=\s*\w+$)/;
        const newRateFixed = newRate < 1 ? (1 / newRate).toFixed(4) : newRate.toFixed(4);
        return currRateText.replace(regex, newRateFixed);
    }

    /**
     * @param isFrom
     * @param changeFee
     * @param currentVal
     * @param hasPromoForFirstPayment
     */
    calcCommission(isFrom, changeFee, currentVal, hasPromoForFirstPayment) {
        const currRateText = this.helpForm.currencyRateText;
        const feeText = this.helpForm.feeText;
        const specialRateOffer = this.commission.specialRateOffer;
        const specialBonusOffer = hasPromoForFirstPayment && this.commission.bonusSpecialRate?.rate;

        const tariffsArray = this.commission.tariffs;

        const tariff = findTariff(this.commission.tariffs, currentVal, isFrom);
        if (!tariff) {
            this.toggleTariffPopup();
            return;
        }

        let comm = !isFrom && tariffsArray.length > 1 ? calcCommToOneCurrency(currentVal, tariff) : calcCommissionBack(currentVal, tariff);
        changeFee(parseFloat(comm).toFixed(2));

        const isCurValLessComm = isFrom && Number(currentVal) <= Number(comm);
        let commForValLessComm;
        if (isCurValLessComm) {
            commForValLessComm = comm;
            comm = currentVal;
        }

        let rate;

        if (this.isSend && specialRateOffer) {
            rate = this.calcBySpecialRateOffer(Number(currentVal), specialRateOffer, isFrom, currRateText, comm);
        } else {
            rate = this.commission.convertRate;
        }

        let otherVal = currentVal / rate + comm;
        if (isFrom) {
            otherVal = (currentVal - comm) * rate;
        }

        if (tariffsArray.length > 1) {
            const newComm = isCurValLessComm ? commForValLessComm : comm;
            const newCurrFeeText = getNewFeeText(newComm, feeText, this.messages.defaultFeeText);
            this.updateCalculatorFieldValue(getFormPropName(this.isSend), FEE_TEXT, newCurrFeeText);
        }

        this.changeFormFieldValue(this.formName, otherVal, isFrom ? AMOUNT_TO : AMOUNT_FROM);
    }
}
